import { memo, useEffect, useRef, useState } from "react";
import FaceFilterMode from "src/api/graphql/__generated__/enums/FaceFilterMode";
import Loading from "src/components/Loading";
import {
	useGatheringStore,
	useParseFilterAndSortOptionsFromGatheringContext,
} from "src/contexts/GatheringContext";
import useGatheringPhotosWithLocal from "src/hooks/useGatheringPhotosWithLocal";
import useStateRef from "src/hooks/useStateRef";
import Photo404 from "src/screens/(error)/Photo404";
import PhotoDetailContent from "../PhotoDetailContent";

const PhotoDetailMainDataManager = ({
	initialPhotoId,
	filterMode,
}: {
	initialPhotoId: string;
	filterMode?: FaceFilterMode;
}) => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const [photosKey, setPhotosKey, photosKeyRef] = useStateRef<string | undefined>(undefined);
	const photoOptions = useParseFilterAndSortOptionsFromGatheringContext();
	const { photos, fetchMore, fetchMoreReverse, loading, refreshing } =
		useGatheringPhotosWithLocal(gatheringId, {
			...photoOptions,
			filterMode,
			baseLimit: 16,
			fromPhotoId: initialPhotoId,
			shouldInitialRefresh: false,
			// If the photo isn't already cached, we swap out the key
			// We need a key to avoid GatheringHome photos being overwritten by refresh
			key: photosKey,
		});
	const [hasInitialKey, setHasInitialKey] = useState(() =>
		photos.some((photo) => photo.photoId === initialPhotoId),
	);
	const prevHasInitialKey = useRef(false);
	useEffect(() => {
		const hasInitialKey = photos.some((photo) => photo.photoId === initialPhotoId);
		if (hasInitialKey !== prevHasInitialKey.current) {
			prevHasInitialKey.current = hasInitialKey;
			setHasInitialKey(hasInitialKey);
		}
	}, [photos, initialPhotoId]);
	const [didKeyFallback, setDidKeyFallback] = useState(false);
	useEffect(() => {
		if (!hasInitialKey && !loading && !photosKeyRef.current) {
			setPhotosKey(initialPhotoId);
			// There's a small amount of desync between when we set the key and when `useGatheringPhotosWithLocal` starts loading
			const timeout = setTimeout(() => {
				setDidKeyFallback(true);
			}, 100);
			return () => clearTimeout(timeout);
		}
		return;
	}, [hasInitialKey, initialPhotoId, loading, photosKeyRef, setPhotosKey]);

	if (!hasInitialKey) {
		return loading || refreshing || !didKeyFallback ? (
			<Loading variant="Screen" />
		) : (
			<Photo404 />
		);
	}
	return (
		<PhotoDetailContent
			photoId={initialPhotoId}
			photos={photos}
			fetchMore={fetchMore}
			fetchMoreReverse={fetchMoreReverse}
		/>
	);
};
export default memo(PhotoDetailMainDataManager);
